import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MapPin, Target, LocateFixed, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const RADIUS_OPTIONS = [10, 25, 50, 100, 250];

export default function NearMeSearch({ filters, setFilters, onSearch, loading }) {
  const [states, setStates] = useState([]);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    api.get("/meta/states").then((r) => setStates(r.data)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!filters.near_city && filters.near_lat === undefined) {
      toast.error("Enter a city or use your current location.");
      return;
    }
    onSearch();
  };

  const useLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFilters((f) => ({
          ...f,
          near_city: "",
          near_state: "",
          near_lat: pos.coords.latitude,
          near_lng: pos.coords.longitude,
          page: 1,
        }));
        setLocating(false);
        // onSearch is triggered by effect in parent
        setTimeout(onSearch, 50);
      },
      () => {
        toast.error("Location permission denied.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const clearRadius = () => {
    setFilters((f) => ({
      ...f,
      near_city: "",
      near_state: "",
      near_lat: undefined,
      near_lng: undefined,
      radius_mi: 50,
      page: 1,
    }));
    // onSearch will be triggered by parent's useEffect since filters changed
  };

  const radiusActive =
    filters.near_city || filters.near_lat !== undefined;

  return (
    <div
      className="border-2 border-zinc-900 bg-zinc-900 text-white"
      data-testid="near-me-search"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-700">
        <div className="flex items-center gap-2">
          <Target size={14} className="text-red-500" />
          <span className="label-eyebrow text-red-500">
            Radius search · Enter your city
          </span>
        </div>
        {radiusActive && (
          <button
            onClick={clearRadius}
            className="text-[11px] uppercase tracking-widest text-zinc-300 hover:text-white"
            data-testid="near-me-clear"
          >
            × Clear
          </button>
        )}
      </div>
      <form onSubmit={submit} className="flex flex-col lg:flex-row">
        <div className="flex items-center gap-3 px-4 py-4 flex-1 border-b lg:border-b-0 lg:border-r border-zinc-700">
          <MapPin size={18} className="text-red-500 flex-shrink-0" />
          <input
            value={filters.near_city || ""}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                near_city: e.target.value,
                near_lat: undefined,
                near_lng: undefined,
                page: 1,
              }))
            }
            placeholder="Your city (e.g., Nashville)"
            className="flex-1 bg-transparent outline-none text-base text-white placeholder:text-zinc-500 font-medium"
            data-testid="near-me-city-input"
          />
        </div>

        <div className="relative border-b lg:border-b-0 lg:border-r border-zinc-700">
          <Select
            value={filters.near_state || "__any"}
            onValueChange={(v) =>
              setFilters((f) => ({
                ...f,
                near_state: v === "__any" ? "" : v,
                page: 1,
              }))
            }
          >
            <SelectTrigger
              className="h-auto rounded-none border-0 bg-zinc-900 text-white py-4 px-4 pr-9 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-800 focus:ring-0 [&>svg]:text-zinc-400"
              data-testid="near-me-state-select"
            >
              <SelectValue placeholder="Any State" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              className="max-h-[60vh] bg-white border border-zinc-300 text-zinc-900"
              data-testid="near-me-state-options"
            >
              <SelectItem value="__any" data-testid="near-me-state-option-any">
                Any State
              </SelectItem>
              {states.map((s) => (
                <SelectItem
                  key={s.code}
                  value={s.code}
                  data-testid={`near-me-state-option-${s.code}`}
                >
                  {s.code} · {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center px-4 py-4 border-b lg:border-b-0 lg:border-r border-zinc-700">
          <label className="label-eyebrow text-zinc-400 mr-3">Radius</label>
          <select
            value={filters.radius_mi || 50}
            onChange={(e) =>
              setFilters((f) => ({ ...f, radius_mi: Number(e.target.value), page: 1 }))
            }
            className="bg-zinc-900 text-white text-sm font-bold outline-none cursor-pointer border-0"
            data-testid="near-me-radius-select"
          >
            {RADIUS_OPTIONS.map((r) => (
              <option key={r} value={r}>{r} miles</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={useLocation}
          disabled={locating}
          className="px-4 py-4 border-b lg:border-b-0 lg:border-r border-zinc-700 text-xs font-semibold uppercase tracking-wider flex items-center gap-2 hover:bg-zinc-800 disabled:opacity-60"
          data-testid="near-me-use-location"
        >
          {locating ? <Loader2 size={12} className="animate-spin" /> : <LocateFixed size={12} />}
          My Location
        </button>

        <button
          type="submit"
          disabled={loading}
          className="px-6 py-4 bg-red-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-red-700 inline-flex items-center gap-2 justify-center disabled:opacity-60"
          data-testid="near-me-submit"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Target size={14} />}
          Find
        </button>
      </form>
    </div>
  );
}
